import { MemorialStories } from "@/components/MemorialStories";
import { OrderLookupBlock } from "@/components/TrackBox";
import { waLink } from "@/components/WhatsApp";
import { ReturnBlock, SizerBlock } from "@/components/CareForms";
import { VipHero, VipNav, VipSteps, VipEarn, VipFaq, vipId } from "@/components/VipBlocks";
import { SplitHero, PageTabs, pillCls } from "@/components/EduBlocks";
import { GlossaryBlock } from "@/components/Glossary";
import { CollectionShowcase } from "@/components/CollectionShowcase";
import { HeroRotator } from "@/components/HeroRotator";
import { Gallery } from "@/components/Gallery";
import { BirthstoneFinder } from "@/components/BirthstoneFinder";
import { Compare } from "@/components/Compare";
import { AmbassadorForm, AmbassadorShowcase } from "@/components/Ambassadors";
import { ContactSplit, IconRow } from "@/components/ContactBlocks";
import { Quotes } from "@/components/Quotes";
import { SaidYes } from "@/components/SaidYes";
import { RevealList } from "@/components/RevealList";
import { MostShared } from "@/components/MostShared";
import { MostSharedPieces } from "@/components/MostSharedPieces";
import { WarrantyClaimBlock } from "@/components/WarrantyClaim";
import { StepNav, TabsStepNav, menuSequence } from "@/components/PageSteps";
import { GiveawayBlock } from "@/components/Giveaway";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Gem, Truck, ShieldCheck, RefreshCcw, MessageCircle, CalendarDays, Phone, Globe, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { visibleColumns } from "@/lib/theme";
import { ProductCard } from "@/components/ProductCard";
import { ShapeIcon } from "@/components/ShapeIcon";
import { Markdown, Stars } from "@/components/common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";

const CTA = ({ label, url, ghost = false, testId }: any) =>
  label && url ? (
    url.startsWith("#") ? <a href={url} data-testid={testId} className={`${ghost ? "btn-line" : "btn-ink"} inline-block px-9 py-4 text-[11px] tracking-[0.25em] uppercase`}>{label}</a>
      : <Link to={url} data-testid={testId} className={`${ghost ? "btn-line" : "btn-ink"} inline-block px-9 py-4 text-[11px] tracking-[0.25em] uppercase`}>{label}</Link>
  ) : null;

function Shapes({ b }: any) {
  const shapes = b.all ? ["Round", "Oval", "Emerald", "Cushion", "Elongated Cushion", "Marquise", "Radiant", "Pear", "Princess", "Asscher", "Heart"] : ["Round", "Oval", "Emerald", "Cushion", "Pear", "Marquise", "Heart", "Princess"];
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-20 md:py-28" data-testid="shop-by-shape">
      <h2 className={b.all ? "font-display uppercase tracking-[0.08em] text-3xl md:text-4xl text-center mb-12" : "font-display text-3xl md:text-4xl font-light mb-12"}>{b.heading}</h2>
      <div className={`grid gap-y-10 ${b.all ? "grid-cols-4 sm:grid-cols-6 lg:grid-cols-11" : "grid-cols-4 md:grid-cols-8"}`}>
        {shapes.map((s) => (
          <Link key={s} to={`/collections/engagement-rings?shape=${s === "Elongated Cushion" ? "Cushion" : s}`} className="group flex flex-col items-center gap-3 text-center hover:text-[var(--c-primary)] transition-colors" data-testid={`shape-link-${s.toLowerCase().replace(" ", "-")}`}>
            <span className="transition-transform duration-500 group-hover:-translate-y-1"><ShapeIcon shape={s} size={46} /></span>
            <span className="eyebrow text-[10px]">{s}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ProductGrid({ b }: any) {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    api.get("/products", { params: { collection: b.collection, limit: b.limit || 8 } }).then((r) => setItems(r.data.items)).catch(() => {});
  }, [b.collection, b.limit]);
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-16" data-testid={`product-grid-${b.collection}`}>
      <div className="flex items-end justify-between mb-10">
        <h2 className="font-display text-3xl md:text-4xl font-light">{b.heading}</h2>
        <Link to={`/collections/${b.collection}`} className="eyebrow link-u">View all</Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12 md:gap-x-8">
        {items.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
      </div>
    </section>
  );
}

function Categories({ b }: any) {
  const { collections } = useSite();
  const [imgs, setImgs] = useState<Record<string, string>>({});
  const picks = ["engagement-rings", "wedding-anniversary", "fine-jewellery", "mens"];
  useEffect(() => {
    picks.forEach((slug) =>
      api.get("/products", { params: { collection: slug, limit: 1 } }).then((r) => setImgs((m) => ({ ...m, [slug]: r.data.items[0]?.image }))).catch(() => {})
    );
  }, []);
  const cols = picks.map((s) => (collections || []).find((c: any) => c.slug === s)).filter(Boolean);
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-16" data-testid="home-categories">
      <h2 className="font-display text-3xl md:text-4xl font-light mb-10">{b.heading}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {cols.map((c: any, i: number) => (
          <Link key={c.slug} to={`/collections/${c.slug}`} className={`group relative bg-white overflow-hidden ${i === 0 ? "md:row-span-2 md:col-span-2 aspect-square md:aspect-auto" : "aspect-square"}`} data-testid={`category-tile-${c.slug}`}>
            {imgs[c.slug] && <img src={imgs[c.slug]} alt={c.title} className="absolute inset-0 w-full h-full object-contain p-6 transition-transform duration-700 group-hover:scale-105" />}
            <span className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-white via-white/80 to-transparent font-display text-xl md:text-2xl">{c.title}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ImageText({ b }: any) {
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-16 md:py-24 grid md:grid-cols-12 gap-10 md:gap-16 items-center" data-testid="image-text-block">
      <div className={`md:col-span-7 ${b.reverse ? "md:order-2" : ""}`}>
        <img src={b.image} alt={b.heading || ""} className="w-full aspect-[4/3] object-cover" style={b.image_focus ? { objectPosition: `50% ${b.image_focus}` } : undefined} loading="lazy" data-testid="image-text-img" />
      </div>
      <div className="md:col-span-5">
        {b.eyebrow && <p className="eyebrow text-[var(--c-primary)] mb-5">{b.eyebrow}</p>}
        <h2 className="font-display text-3xl md:text-5xl font-light leading-tight">{b.heading}</h2>
        {b.body && <p className="mt-6 text-sm md:text-base leading-relaxed text-neutral-600">{b.body}</p>}
        <div className="mt-9 flex flex-wrap gap-4"><CTA label={b.cta_label} url={b.cta_url} testId="image-text-cta" /><CTA label={b.cta2_label} url={b.cta2_url} ghost testId="image-text-cta2" /></div>
      </div>
    </section>
  );
}

function Trust() {
  const t = [
    [Gem, "Certified lab diamonds", "Conflict-free, independently graded"],
    [Truck, "Insured shipping", "Complimentary, signature on delivery"],
    [RefreshCcw, "180-day damage cover", "Plus one complimentary resize"],
    [ShieldCheck, "Lifetime warranty", "Cleaning, checks and care"],
  ];
  return (
    <section className="bg-[var(--c-muted)] my-12" data-testid="trust-bar">
      <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        {t.map(([Icon, h, s]: any) => (
          <div key={h} className="flex flex-col items-center text-center gap-3">
            <Icon size={22} strokeWidth={1.1} className="text-[var(--c-primary)]" />
            <p className="font-display text-xl">{h}</p>
            <p className="text-xs text-neutral-600">{s}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Reviews({ b }: any) {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => { api.get("/reviews/featured").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-16" data-testid="home-reviews">
      <h2 className="font-display text-3xl md:text-4xl font-light mb-10">{b.heading}</h2>
      <div className="grid md:grid-cols-3 gap-8">
        {items.slice(0, 3).map((r) => (
          <figure key={r.id} className="border border-neutral-200 p-8 bg-white">
            <Stars value={r.rating} />
            <p className="font-display text-xl mt-4">{r.title}</p>
            <blockquote className="mt-3 text-sm text-neutral-600 leading-relaxed">{r.body}</blockquote>
            <figcaption className="eyebrow text-[10px] mt-6 text-neutral-500">{r.author} · {r.product_name}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function Text({ b }: any) {
  return <section className="max-w-3xl mx-auto px-5 md:px-0 py-6"><Markdown text={b.body} /></section>;
}

function Cta({ b }: any) {
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-12">
      <div className="bg-[var(--c-fg)] text-[var(--c-bg)] px-8 md:px-16 py-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
        <p className="font-display text-3xl md:text-4xl font-light">{b.heading}</p>
        <Link to={b.cta_url} className="bg-[var(--c-bg)] text-[var(--c-fg)] px-9 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[var(--c-primary)] hover:text-white transition-colors" data-testid="cta-block-button">{b.cta_label}</Link>
      </div>
    </section>
  );
}

function Faq({ b }: any) {
  return (
    <section className="max-w-3xl mx-auto px-5 py-10">
      {b.heading && <h2 className="font-display text-3xl mb-6">{b.heading}</h2>}
      <Accordion type="single" collapsible>
        {(b.items || []).map((f: any, i: number) => (
          <AccordionItem key={i} value={`f${i}`} data-testid={`faq-item-${i}`}><AccordionTrigger className="font-display text-lg text-left" data-testid={`faq-trigger-${i}`}>{f.question}</AccordionTrigger><AccordionContent className="text-neutral-600">{f.answer}</AccordionContent></AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

const slugId = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

function Banner({ b }: any) {
  return (
    <section className={`relative w-full overflow-hidden ${b.video ? "h-[60vh] md:h-[78vh]" : "h-[34vh] md:h-[46vh]"}`} data-testid="banner-block">
      {b.image && <img src={b.image} alt="" className="absolute inset-0 w-full h-full object-cover" style={b.image_position ? { objectPosition: b.image_position } : undefined} />}
      {b.video && <video src={b.video} poster={b.image} autoPlay muted loop playsInline preload="auto" className={`absolute inset-0 w-full h-full object-cover ${b.video_mobile ? "hidden md:block" : ""}`} data-testid="banner-video" />}
      {b.video_mobile && <video src={b.video_mobile} autoPlay muted loop playsInline preload="auto" className="absolute inset-0 w-full h-full object-cover md:hidden" data-testid="banner-video-mobile" />}
      <div className={`absolute inset-0 ${b.video ? "bg-black/45" : "bg-black/25"}`} />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 text-white rise">
        {b.eyebrow && <p className="eyebrow text-[10px] md:text-xs mb-4" data-testid="banner-eyebrow">{b.eyebrow}</p>}
        {b.heading && <h1 className="font-display font-light uppercase tracking-[0.12em] text-4xl sm:text-5xl lg:text-6xl">{b.heading}</h1>}
        {b.subheading && <p className="mt-5 max-w-xl text-sm md:text-base" data-testid="banner-subheading">{b.subheading}</p>}
      </div>
    </section>
  );
}

const anchorItems = (all: any[]) => (all || []).filter((x: any) => !x.hide_from_nav && (((x.type === "split" || x.type === "contact_split") && x.heading) || ((x.type === "vip_steps" || x.type === "vip_faq") && x.nav_label)));
const anchorId = (x: any) => (x.type.startsWith("vip_") ? vipId(x) : slugId(x.heading));

function AnchorNav({ all }: any) {
  const items = anchorItems(all);
  const aid = anchorId;
  const [active, setActive] = useState("");
  useEffect(() => {
    let raf = 0;
    const spy = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        let cur = "";
        items.forEach((x: any) => { const el = document.getElementById(aid(x)); if (el && el.getBoundingClientRect().top <= window.innerHeight * 0.4) cur = el.id; });
        setActive(cur);
      });
    };
    spy();
    window.addEventListener("scroll", spy, { passive: true });
    return () => { window.removeEventListener("scroll", spy); cancelAnimationFrame(raf); };
  }, [all]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length) return null;
  const go = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <>
      <div className="md:hidden px-5 py-4 border-y border-neutral-200 bg-[var(--c-bg)] sticky top-16 z-30" data-testid="anchor-nav-mobile">
        <select defaultValue="" onChange={(e) => { if (e.target.value) go(e.target.value); e.target.value = ""; }} className="w-full border border-neutral-900 bg-transparent px-4 py-3 eyebrow outline-none" data-testid="anchor-nav-select">
          <option value="" disabled>Jump to section</option>
          {items.map((x: any) => <option key={aid(x)} value={aid(x)}>{x.nav_label || x.heading}</option>)}
        </select>
      </div>
      <nav className="border-b border-neutral-200 bg-[var(--c-bg)] sticky top-[124px] z-30 hidden md:block" data-testid="anchor-nav">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-wrap justify-center gap-x-7 gap-y-0">
          {items.map((x: any) => { const id = aid(x); return <a key={id} href={`#${id}`} className={`eyebrow text-[10px] py-4 whitespace-nowrap border-b-2 transition-colors ${(active || aid(items[0])) === id ? "border-neutral-900" : "border-transparent hover:text-[var(--c-primary)]"}`} data-testid={`anchor-${id}`}>{x.nav_label || x.heading}</a>; })}
        </div>
      </nav>
    </>
  );
}

function Intro({ b }: any) {
  const H = b.level === "h1" ? "h1" : "h2";
  return (
    <section className={b.shaded ? "bg-[var(--c-muted)] px-5 py-14 md:py-20" : ""} data-testid="intro-block">
      <div className={`max-w-3xl mx-auto text-center ${b.shaded ? "" : "px-5 pt-14 md:pt-20 pb-6"}`}>
        {b.heading && <H className={`font-display uppercase tracking-[0.08em] ${H === "h1" && !b.shaded ? "text-4xl sm:text-5xl" : "text-3xl md:text-4xl"}`}>{b.heading}</H>}
        {b.subheading && <p className={`font-display uppercase tracking-[0.08em] text-2xl md:text-3xl ${b.heading ? "mt-4" : ""}`} data-testid="intro-subheading">{b.subheading}</p>}
        {b.body && <div className="mt-6 text-[15px] md:text-base leading-relaxed"><Markdown text={b.body} /></div>}
      </div>
    </section>
  );
}

function Split({ b }: any) {
  if (b.centered) return <SplitCentered b={b} />;
  return (
    <section id={slugId(b.heading)} className={`mx-auto px-5 md:px-12 ${b.compact ? "py-8 md:py-10" : "py-14 md:py-20"} grid gap-10 md:gap-20 ${b.sticky ? "items-start" : "items-center"} scroll-mt-48 ${b.image ? (b.compact ? `max-w-[1100px] ${b.reverse ? "md:grid-cols-[1fr_320px]" : "md:grid-cols-[320px_1fr]"} md:gap-14` : "max-w-[1400px] md:grid-cols-2") : b.wide ? "max-w-[1300px]" : "max-w-3xl"}`} data-testid="split-block">
      {b.image && <div className={`${b.reverse ? "md:order-2" : ""} ${b.sticky ? "md:sticky md:top-44" : ""} ${b.compact ? "max-w-[320px] w-full mx-auto md:mx-0" : ""}`}><img src={b.image} alt="" loading="lazy" className={`w-full ${b.compact ? "aspect-square" : b.portrait ? "aspect-[4/5]" : "aspect-[5/4]"} object-cover`} /></div>}
      <div>
        {b.eyebrow && <p className="font-display text-2xl md:text-3xl text-[var(--c-primary)] mb-3" data-testid="split-eyebrow">{b.eyebrow}</p>}
        <h2 className={`font-display uppercase tracking-[0.08em] ${b.wide || b.sticky ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"}`}>{b.heading}</h2>
        {b.subheading && <p className="mt-5 font-medium text-base md:text-lg">{b.subheading}</p>}
        {b.body && <div className={b.wide || b.sticky ? "mt-6 text-sm md:text-[15px]" : "mt-4"}><Markdown text={b.body} /></div>}
        <RevealList label={b.reveal_label} items={b.reveal_items} id={slugId(b.heading)} />
        <div className="mt-6 flex flex-wrap gap-4"><CTA label={b.cta_label} url={b.cta_url} testId="split-cta" /><CTA label={b.cta2_label} url={b.cta2_url} ghost testId="split-cta2" /></div>
      </div>
    </section>
  );
}

function SplitCentered({ b }: any) {
  const id = slugId(b.heading);
  return (
    <section id={id} className={`mx-auto px-5 md:px-12 py-8 md:py-12 grid gap-8 md:gap-16 items-center scroll-mt-48 ${b.image ? "max-w-[1200px] md:grid-cols-2" : "max-w-2xl"}`} data-testid={`split-centered-${id}`}>
      {b.image && <div className={b.reverse ? "md:order-2" : ""}><img src={b.image} alt="" loading="lazy" className="w-full aspect-[6/5] object-cover" /></div>}
      <div className="text-center max-w-md mx-auto">
        <h2 className="font-display uppercase tracking-[0.08em] text-2xl md:text-3xl leading-snug">{b.heading}</h2>
        {b.subheading && <p className="mt-4 font-medium text-sm">{b.subheading}</p>}
        {b.body && <div className="mt-5 text-[13px] md:text-sm leading-relaxed [&_ul]:list-inside [&_ul]:pl-0 [&_ul]:space-y-1"><Markdown text={b.body} /></div>}
        {b.cta_label && b.cta_url && <Link to={b.cta_url} className={`${pillCls} mt-7`} data-testid={`split-cta-${id}`}>{b.cta_label}</Link>}
      </div>
    </section>
  );
}

function Tiles({ b }: any) {
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-16" data-testid="collection-tiles">
      {b.heading && <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl text-center mb-10">{b.heading}</h2>}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {(b.items || []).map((t: any, i: number) => (
          <Link key={i} to={t.url || "/"} className="group block" data-testid={`tile-${slugId(t.label)}`}>
            <div className="aspect-[4/5] overflow-hidden bg-[var(--c-muted)]">{t.image && <img src={t.image} alt={t.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}</div>
            <p className="mt-3 text-center eyebrow">{t.label}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Help({ b }: any) {
  const wa = waLink(useSite().site?.whatsapp);
  const opts = [
    ["Live chat", "", b.chat_url || wa, MessageCircle], ["Appointment", "", b.booking_url || "/consultation", CalendarDays],
    [b.phone_label || "Toll Free", b.phone, b.phone && `tel:${b.phone.replace(/[^+\d]/g, "")}`, Phone],
    ["International", b.phone_intl, b.phone_intl && `tel:${b.phone_intl.replace(/[^+\d]/g, "")}`, Globe],
    ["Email", b.email, b.email && `mailto:${b.email}`, Mail],
  ].filter(([, , u]) => u);
  return (
    <section className="bg-[var(--c-muted)] mt-10" data-testid="help-block">
      <div className="max-w-[1100px] mx-auto px-5 md:px-12 py-16 text-center">
        <p className="eyebrow text-[var(--c-primary)]">Contact us</p>
        <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl mt-3">{b.heading || "How can we help?"}</h2>
        {b.text && <p className="mt-4 text-neutral-600">{b.text}</p>}
        <div className={`mt-10 grid grid-cols-2 gap-4 ${opts.length >= 5 ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
          {opts.map(([l, sub, u, Icon]: any) => (
            <a key={l} href={u} className="group btn-line py-6 px-3 flex flex-col items-center gap-2.5" data-testid={`help-${slugId(l)}`}>
              <Icon size={22} strokeWidth={1.2} className="transition-transform group-hover:-translate-y-0.5" />
              <span className="text-xs tracking-[0.2em] uppercase">{l}</span>
              {sub && <span className="text-[11px] tracking-normal normal-case opacity-80">{sub}</span>}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function SitemapBlock({ b }: any) {
  const [d, setD] = useState<any>(null);
  useEffect(() => { if (b.auto !== false) api.get("/sitemap-data").then((r) => setD(r.data)).catch(() => {}); }, [b.auto]);
  const auto = d ? [
    { title: "Shop", links: [...d.collections, { label: "Design Your Own Ring", url: "/ring-builder" }] },
    { title: "Learn", links: [{ label: "Education", url: "/education" }, ...d.education, { label: "FAQ", url: "/faq" }] },
    { title: "Services", links: [{ label: "Book a Consultation", url: "/consultation" }, { label: "Showrooms", url: "/stores" }, { label: "Financing", url: "/financing" }, { label: "Track an Order", url: "/track" }, { label: "My Account", url: "/account" }] },
    { title: "Company & Care", links: d.pages },
    { title: "Journal", links: [{ label: "All Articles", url: "/blog" }, ...d.posts] },
  ] : [];
  const groups = [...visibleColumns(b.groups), ...(b.auto !== false ? auto : [])];
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-14" data-testid="sitemap-block">
      {b.heading && <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl mb-12">{b.heading}</h2>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
        {groups.map((g: any, i: number) => (
          <div key={g.title + i} data-testid={`sitemap-group-${slugId(g.title)}`}>
            <p className="uppercase font-medium tracking-[0.08em] mb-4 pb-3 border-b border-neutral-200">{g.title}</p>
            <ul className="space-y-2">
              {(g.links || []).map((l: any) => <li key={l.label + l.url}><Link to={l.url} className="text-[15px] tracking-[0.04em] text-neutral-700 hover:text-[var(--c-primary)] transition-colors">{l.label}</Link></li>)}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

const MAP: Record<string, any> = { giveaway: GiveawayBlock, hero: HeroRotator, shapes: Shapes, product_grid: ProductGrid, categories: Categories, image_text: ImageText, trust: Trust, reviews: Reviews, text: Text, cta: Cta, faq: Faq,
  banner: Banner, intro: Intro, split: Split, collection_tiles: Tiles, collection_showcase: CollectionShowcase, help: Help, sitemap: SitemapBlock, memorial_stories: MemorialStories, order_lookup: OrderLookupBlock, return_request: ReturnBlock, ring_sizer: SizerBlock,
  vip_hero: VipHero, vip_steps: VipSteps, vip_earn: VipEarn, vip_faq: VipFaq, said_yes: SaidYes, edu_hero: SplitHero, page_tabs: PageTabs, glossary: GlossaryBlock, contact_split: ContactSplit, icon_row: IconRow, quotes: Quotes, most_shared: MostShared, most_shared_products: MostSharedPieces, warranty_claim: WarrantyClaimBlock, gallery: Gallery, birthstone_finder: BirthstoneFinder, compare: Compare, ambassador_form: AmbassadorForm, ambassador_showcase: AmbassadorShowcase };

export function Blocks({ blocks }: { blocks: any[]; slug?: string }) {
  const { site } = useSite() as any;
  const off: string[] = site?.page_nav_off || [];
  const tabs = (blocks || []).find((b) => b.type === "page_tabs");
  const tabsOn = tabs && !off.includes((tabs.items || []).find((x: any) => x.url)?.url);
  let tail = (blocks || []).length;
  while (tail > 0 && ["cta", "help"].includes(blocks[tail - 1].type)) tail--;
  const { pathname, state } = useLocation() as any;
  const seq = menuSequence(site?.header_menu, pathname, state?.menu);
  const menuOn = !!seq && !off.includes(seq.key);
  const anchors = anchorItems(blocks);
  // Buttons sit at the end of the page's own text and images, above trailing CTA / help blocks.
  const at = menuOn ? (anchors.length && (blocks || []).some((b) => b.type === "anchor_nav") ? blocks.indexOf(anchors[anchors.length - 1]) + 1 : tail) : -1;
  const menuNav = menuOn ? <StepNav key="menu-step" prev={seq!.prev} next={seq!.next} state={{ menu: seq!.key }} testId="page-step-nav" /> : null;
  return <>{(blocks || []).map((b, i) => {
    const step = i === at ? menuNav : !menuOn && tabsOn && i === tail ? <TabsStepNav key={`tabs-${i}`} tabs={tabs} /> : null;
    if (b.type === "anchor_nav") return <AnchorNav key={i} all={blocks} />;
    if (b.type === "vip_nav") return <VipNav key={i} all={blocks} />;
    const C = MAP[b.type];
    return <React.Fragment key={i}>{step}{C ? <C b={b} /> : null}</React.Fragment>;
  })}{menuOn ? at === (blocks || []).length && menuNav : tabsOn && tail === (blocks || []).length && <TabsStepNav tabs={tabs} />}</>;
}
